/** 船名展示：BLANK SAILING → 空班 */
export function formatVesselName(vessel) {
  if (!vessel) return '-'
  if (isBlankSailing(vessel)) return '空班'
  return vessel
}

/** 是否空班船期（原始 BLANK SAILING 或已展示为「空班」） */
export function isBlankSailing(vessel) {
  const text = String(vessel || '').trim()
  if (!text) return false
  const upper = text.toUpperCase()
  return upper === 'BLANK SAILING' || text === '空班'
}

/** 解析港口名称（兼容 sys_dict JSON 字符串） */
export function parsePortNameLabel(raw) {
  if (!raw) return ''
  const str = String(raw).trim()
  if (!str) return ''
  if (str.startsWith('{')) {
    try {
      const obj = JSON.parse(str)
      return obj.portName || obj.englishName || ''
    } catch (e) {
      return str
    }
  }
  return str
}

/** 港口展示名去掉末尾括号内的五字码（如 南沙(CNNSA) → 南沙） */
export function formatPortLabelWithoutCode(raw) {
  const name = parsePortNameLabel(raw)
  if (!name) return ''
  return String(name).replace(/\([A-Za-z0-9]{3,8}\)\s*$/, '').trim()
}

/** 解析本地费查询用的航线 route（与本地费设置、船期复制逻辑一致） */
export function resolveLocalFeeRoute(rawPodName) {
  if (!rawPodName) return ''
  const str = String(rawPodName).trim()
  if (!str) return ''
  if (str.startsWith('{')) {
    try {
      const obj = JSON.parse(str)
      return obj.route || ''
    } catch (e) {
      return ''
    }
  }
  return str
}

/** 港口展示：中文名(五字码) */
export function formatPortDisplay(name, code) {
  const label = parsePortNameLabel(name)
  if (label && code) return `${label}(${code})`
  return label || code || '-'
}

/** 去掉港口展示名中的 (五字码) 后缀，保留纯中文名 */
export function stripPortCodeSuffix(displayName) {
  if (!displayName) return ''
  const text = String(displayName).trim()
  const matched = text.match(/^(.+?)\s*\([A-Z0-9]+\)$/)
  return matched ? matched[1].trim() : text
}

/**
 * 中转港 allPort 展示：MYTPP-LKCMB → 中文名-中文名
 * resolvePortName: (dictCode) => 展示名，通常来自 getPortDisplayName
 */
export function formatAllPortDisplay(allPort, resolvePortName, labelMap = {}) {
  if (!allPort) return '-'
  if (labelMap[allPort]) return labelMap[allPort]
  const parts = String(allPort)
    .split(/[-－—–]/)
    .map(item => item.trim())
    .filter(Boolean)
  if (!parts.length) return allPort
  const labels = parts.map(code => {
    const mapped = labelMap[code]
    if (mapped) return mapped
    const raw = resolvePortName ? resolvePortName(code) : code
    if (!raw || raw === code) return code
    return stripPortCodeSuffix(raw) || raw
  })
  return labels.join('-')
}

const TRANSIT_LEVEL_LABELS = ['一转', '二转', '三转', '四转', '五转']

/**
 * 将中转港字符串拆成层级列表
 * 例："蛇口-香港-高雄" → [{ label: '一转', port: '蛇口' }, ...]
 */
export function parseTransitLevels(transitPort) {
  if (!transitPort) return []
  const ports = String(transitPort)
    .split(/[-－—–]/)
    .map(item => item.trim())
    .filter(Boolean)
  return ports.map((port, index) => ({
    label: TRANSIT_LEVEL_LABELS[index] || `${index + 1}转`,
    port
  }))
}

/**
 * 解析航程天数，区间取最大值。
 * 支持：4、4天、4-10天、4~10天、4.5 等
 */
export function parseMaxTransitDays(raw) {
  if (raw === null || raw === undefined || raw === '') return null
  const text = String(raw).trim()
  if (!text || text === '未识别') return null
  const nums = text.match(/\d+(?:\.\d+)?/g)
  if (!nums || !nums.length) return null
  const max = Math.max(...nums.map(Number).filter(n => !Number.isNaN(n) && n >= 0))
  if (!Number.isFinite(max)) return null
  return Math.round(max)
}

/** 订舱/详情航程展示：区间取最大天数，如 22-24 → 24天 */
export function formatTransitDaysDisplay(raw) {
  if (raw === '' || raw == null) return '-'
  const maxDays = parseMaxTransitDays(raw)
  if (maxDays !== null) return `${maxDays}天`
  const text = String(raw).trim()
  if (!text) return '-'
  return text.includes('天') ? text : `${text}天`
}

function formatYmd(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * 根据预计开船时间 + 航程计算预计到港（yyyy-MM-dd）
 */
export function calcEtaByEtdAndTransit(etd, transitRaw) {
  if (!etd) return ''
  const days = parseMaxTransitDays(transitRaw)
  if (days === null) return ''
  const text = String(etd).trim()
  const matched = text.match(/(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (matched) {
    const date = new Date(Number(matched[1]), Number(matched[2]) - 1, Number(matched[3]) + days)
    return formatYmd(date)
  }
  const date = new Date(etd)
  if (Number.isNaN(date.getTime())) return ''
  date.setDate(date.getDate() + days)
  return formatYmd(date)
}

/**
 * 查看船期时的预计到港：按当前航程 + 预计开船推算，无法推算时回退原 eta
 */
export function formatEtaByVoyage(scheduleRow, transitRaw) {
  const calculated = calcEtaByEtdAndTransit(scheduleRow && scheduleRow.etd, transitRaw)
  if (calculated) return calculated
  const eta = scheduleRow && scheduleRow.eta
  if (!eta) return ''
  const text = String(eta)
  return text.length > 10 ? text.substring(0, 10) : text
}

/** 日期展示为 MM-DD（去掉年份） */
export function formatMd(value) {
  if (value === null || value === undefined || value === '') return ''
  const text = String(value).trim()
  const matched = text.match(/\d{4}-(\d{2}-\d{2})/)
  if (matched) return matched[1]
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return text.length >= 10 ? text.substring(5, 10) : text
  }
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${month}-${day}`
}

/** 查看船期 ETD/ETA 列：航程推算后仅展示月-日 */
export function formatEtaByVoyageMd(scheduleRow, transitRaw) {
  return formatMd(formatEtaByVoyage(scheduleRow, transitRaw))
}

/**
 * 订舱/详情展示 ETA：ETD + 航程（区间取最大天数）；无法推算时回退原 eta
 */
export function resolveDisplayEta(etd, transitRaw, fallbackEta) {
  const calculated = calcEtaByEtdAndTransit(etd, transitRaw)
  if (calculated) return calculated
  if (!fallbackEta) return ''
  const text = String(fallbackEta)
  return text.length > 10 ? text.substring(0, 10) : text
}

/** 根据船名列表计算表格「船名」列 width */
export function calcVesselColumnWidth(vesselList, options = {}) {
  const min = options.min || 100
  const max = options.max || 300
  const charWidth = options.charWidth || 10
  const padding = options.padding || 34

  let maxLen = 2
  ;(vesselList || []).forEach(v => {
    const text = formatVesselName(v)
    if (text && text !== '-') {
      maxLen = Math.max(maxLen, String(text).length)
    }
  })
  return Math.min(max, Math.max(min, maxLen * charWidth + padding))
}